import { List } from 'antd';
import clsx from 'clsx';
import type { CSSProperties, MouseEvent, ReactNode } from 'react';
import styles from './FeedCard.module.scss';

const STAGGER_STEP_MS = 50;
const STAGGER_MAX_MS = 180;

type Props = {
  children: ReactNode;
  /** Color of the 3px left accent stripe. Defaults to the secondary border color. */
  accent?: string;
  /** Position in the feed — drives the staggered slide-up entrance. */
  index?: number;
  /** Stack children in a column (stretched). Defaults to a top-aligned row. */
  vertical?: boolean;
  /** Dim the card, e.g. for closed/terminal items. */
  dimmed?: boolean;
  onClick?: () => void;
  className?: string;
};

/**
 * Shared feed card shell for idea and praise rows: bordered surface with a
 * colored left accent, staggered entrance, and hover shadow. Callers own the
 * inner content; `vertical` switches the layout between a row and a column.
 */
export const FeedCard = ({
  children,
  accent,
  index = 0,
  vertical = false,
  dimmed = false,
  onClick,
  className,
}: Props) => {
  const delay = Math.min(index * STAGGER_STEP_MS, STAGGER_MAX_MS);
  // Links rendered inside the card (e.g. markdown bodies) must not also trigger the card's navigation.
  const handleClick = (e: MouseEvent<HTMLDivElement>) => {
    if (e.target instanceof Element && e.target.closest('a')) return;
    onClick?.();
  };

  return (
    <List.Item
      className={clsx(
        styles.card,
        vertical ? styles.vertical : styles.horizontal,
        dimmed && styles.dimmed,
        onClick && styles.clickable,
        className,
      )}
      onClick={onClick && handleClick}
      style={
        {
          '--accent': accent,
          '--row-delay': `${delay}ms`,
        } as CSSProperties
      }
    >
      {children}
    </List.Item>
  );
};
